/**
 * AuthError
 */

import isPlainObject from 'lodash-es/isPlainObject'

/**
 * An error dictionary returned from Auth APIs with additional props
 *
 * @privateRemarks
 * todo: Make this extend Error like TOP2's PlayerError
 */
export interface AuthError {
  /**
   * {@link AuthErrorCode} a string uniquely identifying the source of the error
   */
  code: AuthErrorCode
  /**
   * {@link AuthErrorSeverity} how bad the error is
   */
  severity: AuthErrorSeverity
  /**
   * {string} describing the error
   */
  message: string
  /**
   * {@link AuthErrorMetadata} a hash/map/dictionary of additional key/values
   */
  metadata?: AuthErrorMetadata
  /**
   * Pretty prints the `error` information to a `String` which will show
   * things like the error `code`, `severity`, and other error metadata
   */
  toString(): string
}

export const isAuthError = (error: unknown): error is AuthError => {
  const authError = error as AuthError
  return (
    isPlainObject(authError)
    && 'code' in (authError)
    && 'severity' in (authError)
    && 'message' in (authError)
  )
}

/** The AuthErrorSeverity of the {@link AuthError} */
export enum AuthErrorSeverity {
  /**
   * This error is potentially recoverable by retrying. For example,
   * if the network is down, the client may attempt to fetch the data again once the network is available.
   */
  Error = 'error',
  /**
   * This error is purely informative. The Auth lifecycle can likely continue,
   * although with potential caveats or risks.
   */
  Warn = 'warn',
  /**
   * Errors of this nature always represent irrecoverable errors.
   * The Auth SDK must be torn down and authentication cannot be attempted again without user input.
   */
  Fatal = 'fatal'
}

/**
 * `AuthErrorCategory` = what was going on when the error occurred
 * @hidden
 */
export enum AuthErrorCategory {
  Unknown = 'UN',
  Initialization = 'IT',
  Config = 'CF',
  Authentication = 'AN',
  Authorization = 'AZ',
  Preauthorization = 'PA',
  Metadata = 'MD',
  Logout = 'LO',
}

/**
 * `AuthErrorPlatform` = where the error occurred
 * @hidden
 */
export enum AuthErrorPlatform {
  Unknown = '00',
  Web = '01',
  iOS = '02',
  Android = '03',
  ReactNative = '04',
}

/**
 * `AuthErrorDomain` = who to blame for the error
 * @hidden
 */
export enum AuthErrorDomain {
  Unknown = 'UN',
  Target = 'TA',
  Shared = 'SH',
  NativeService = 'NS',
  Nexus = 'NX',
  Adobe = 'AE',
  Runtime = 'RT',
}

/**
 * `AuthErrorNumeral` = uniquely identify the cause of the failure
 * @hidden
 * TODO: sync with iOS: AuthBlock/Model/AuthError.swift
 * TODO: sync with Android: com/turner/top/auth/model/AuthError.kt
 */
export enum AuthErrorNumeral {
  Unspecified = '0000',

  /** Constructing or making the request for MVPD Config failed */
  MvpdConfigRequestFailure = '0100',

  /** Failed to obtain or process the response from MVPD Config */
  MvpdConfigResponseFailure = '0101',

  /**
   * If you attempt to select a Provider not enabled in the MVPD Config.
   * It is also possible to be authN'ed passively via SSO with a provider not in the MVPD Config.
   */
  MvpdConfigProviderNotFound = '0102',

  /** iOS: `buildImageURL()` failure: invalid URL */
  UnparseableImageURL = '0111',

  /** iOS: `buildImageURL()` failure: network error */
  UnreachableImageURL = '0112',

  /** iOS: `buildImageURL()` failure: could not build image */
  UnqualifiedImageURL = '0113',

  /** Uncaught `Error` thrown during `prepare()` */
  AdobeInitializationFailure = '0200',

  /** If you use an invalid software statement or an invalid requestor ID */
  AdobeSetRequestorFailure = '0201',

  /** Uncaught `Error` thrown during `fetchAuthContext()` or `login()` */
  AdobeAuthenticationFailure = '0202',

  /** In the unlikely event that AccessEnabler still says you're authenticated post-`logout()` */
  AdobeLogoutFailure = '0203',

  /** In the unlikely event that AccessEnabler provides an empty response */
  AdobeAuthorizationMissingData = '0207',

  /** In the unlikely event that AccessEnabler responds with a different schema */
  AdobeAuthorizationInvalidData = '0208',

  /** If you attempt to `cancelAuthentication()` when login procedure is not in progress */
  CallOutsideOfAuthenticationFlow = '0400',

  /**
   * Scenarios with this response:
   * - if you attempt to `logout()` when you're not logged in
   * - if you attempt to `fetchUserMetadata()` when you're not logged in
   * - if you attempt to `authorize()` when you're not logged in
   */
  NotAuthenticated = '0401',

  /** If you are still authenticated in system settings */
  AuthenticatedInSystemSettings = '0402',

  /** iOS: exceeded maxAuthenticationPollAttempts */
  AuthenticationTimeout = '0403',

  /** If you attempt to use SSO in iOS Simulator */
  SsoUnavailableInSimulator = '0410',

  /** If you attempt to use SSO for a provider who doesn't support it */
  SsoUnsupportedByProvider = '0411',

  /** [iOS] SSO Permissions were denied */
  SsoPermissionsDenied = '0412',

  /** [iOS] The user has chosen another provider outside of their stored SSO provider */
  UserChoseAnotherProvider = '0413',

  /** Picker: Unknown handler found in UrlResponse */
  PickerUnknownUrlResponseHandler = '0420',

  /** Picker: Invalid URL found in UrlResponse */
  PickerInvalidUrlResponseRedirectUrl = '0421',

  /** Picker: URL missing from UrlResponse */
  PickerMissingUrlResponseRedirectUrl = '0422',

  /** Picker: Failed to attach WebView */
  PickerCannotAttachWebView = '0423',

  /** Picker: Failed to load WebView */
  PickerWebViewFailedToLoad = '0424',

  /** Picker: Failed to load SafariViewController */
  PickerSafariViewControllerFailedToLoad = '0425',

  /** Picker: Failed to configure WebView */
  PickerFailedToConfigureWebView = '0426',

  /** Picker: WebView did not load after timeout */
  PickerWebViewDidNotLoad = '0427',

  /** If your account with your Provider does not have authorization to watch the requested content */
  NotAuthorized = '0501',

  ClientlessClientRegistrationInvalidRedirectUri = '0599',
  ClientlessUnknownError = '0600',
  ClientlessAPIUninitialized = '0601',
  ClientlessAPIBadRequest = '0602',
  ClientlessAPIUnexpectedResponseCode = '0603',
  ClientlessMissingSoftwareStatement = '0604',

  ClientlessClientRegistrationServiceException = '0605',
  ClientlessClientRegistrationInvalidResponseBody = '0606',

  ClientlessClientAccessTokenServiceException = '0607',
  ClientlessClientAccessTokenInvalidResponseBody = '0608',

  ClientlessAPIForbiddenMethod = '0609',

  ClientlessConfigServiceException = '0610',
  ClientlessConfigInvalidResponseBody = '0611',
  ClientlessConfigParseError = '0612',

  ClientlessRegCodeServiceException = '0620',
  ClientlessRegCodeInvalidResponseBody = '0621',
  ClientlessRegCodeNotFound = '0622',

  ClientlessCheckAuthNServiceException = '0630',
  ClientlessCheckAuthNForbidden = '0631',
  ClientlessCheckAuthNCanceled = '0632',

  ClientlessAuthNTokenServiceException = '0634',
  ClientlessAuthNTokenParseError = '0635',
  ClientlessAuthNTokenNotFound = '0636',
  ClientlessAuthNTokenExpired = '0637',
  ClientlessInvalidClient = '0638',
  ClientlessAccessDenied = '0639',

  ClientlessAuthorizeServiceException = '0640',
  ClientlessAuthorizeParseError = '0641',
  ClientlessAuthorizeNotAuthorized = '0642',

  ClientlessAuthZTokenServiceException = '0644',
  ClientlessAuthZTokenParseError = '0645',
  ClientlessAuthZTokenNotAuthenticated = '0646',
  ClientlessAuthZTokenExpired = '0647',
  ClientlessAuthZTokenNotAuthorized = '0648',

  ClientlessShortMediaTokenServiceException = '0650',
  ClientlessShortMediaTokenParseError = '0651',
  ClientlessShortMediaTokenNotAuthorized = '0652',

  ClientlessPreauthorizeServiceException = '0654',
  ClientlessPreauthorizeNoResourcesProvided = '0655',
  ClientlessPreauthorizeNotAuthorized = '0656',
  ClientlessPreauthorizeExpired = '0657',
  ClientlessPreauthorizeNotAuthenticated = '0658',

  ClientlessUserMetadataServiceException = '0680',
  ClientlessUserMetadataParseError = '0681',
  ClientlessUserMetadataNotFound = '0682',
  ClientlessUserMetadataExpiredToken = '0683',

  ClientlessGetAuthenticationUrl = '0690',
  ClientlessGetAuthenticationUrlMethodNotAllowed = '0691',
  ClientlessGetAuthenticationUrlInternalServerError = '0692',
  ClientlessGetAuthenticationUrlInvalidResponseBody = '0693',

  ClientlessResumeAuthenticationUrl = '0694',
  ClientlessResumeAuthenticationUrlMethodNotAllowed = '0695',
  ClientlessResumeAuthenticationUrlInternalServerError = '0696',
  ClientlessResumeAuthenticationUrlInvalidResponseBody = '0697',

  /** Auth engine failed to init or has not been init'ed yet */
  InternalFailure = '0900',

  /** If you provide incorrect/invalid parameters to an Auth API */
  TypeError = '0901',

  /** Incapable of further communication */
  BridgeClosed = '0902',

  /** Browser is lacking SessionStorage or other required feature **/
  UnsupportedBrowser = '0903'
}


/**
 * An alphanumeric string constructed of substrings representing
 * `[Category]` `[Platform]` `[Domain]` `[Numeral]`
 *
 * @see {@link AuthError.code}
 */
export type AuthErrorCode = string

/**
 * The ErrorCodeBuilder normalizes the steps to building an {@link AuthErrorCode}
 * @hidden
 */
export class AuthErrorCodeBuilder {
  private _category: AuthErrorCategory
  private _platform: AuthErrorPlatform
  private _domain: AuthErrorDomain
  private _numeral: AuthErrorNumeral

  /**
   * Creates an instance of ErrorCodeBuilder.
   *
   * @param {AuthErrorCategory}_category   `[CF]10AE100`
   * @param {AuthErrorPlatform} _platform  `CF[10]AE100`
   * @param {AuthErrorDomain} _domain       `CF10[AE]100`
   * @param {AuthErrorNumeral} _numeral     `CF10AE[100]`
   */
  constructor(category: AuthErrorCategory, platform: AuthErrorPlatform, domain: AuthErrorDomain, numeral: AuthErrorNumeral) {
    this._category = category
    this._platform= platform
    this._domain = domain
    this._numeral = numeral
  }

  /** Builds the {@link AuthErrorCode} that has been configured */
  build(): AuthErrorCode {
    return `${this._category}${this._platform}${this._domain}${this._numeral}`
  }
}

/** Optional additional information regarding the {@link AuthError} */
export interface AuthErrorMetadata {
  /**  A suggestion to recover if recoverable */
  recoverySuggestion?: string

  /** A sub-service error code; this may have Adobe-specified AccessEnabler error codes */
  subErrorCode?: string

  /** A sub-service error message; in the case of authZ failures, this could have a Provider-specified message */
  subErrorMessage?: string
  
  /** The endpoint url called which triggered the error */
  subErrorUrl?: string
}

enum AuthErrorMetadataProperty {
  RecoverySuggestion = 'recoverySuggestion',
  SubErrorCode = 'subErrorCode',
  SubErrorMessage = 'subErrorMessage',
  SubErrorUrl = 'subErrorUrl',
}

const getPlatform = (): AuthErrorPlatform => AuthErrorPlatform.Web

/**
 * A fluent builder of {@link AuthError}
 * @hidden
 */
export class AuthErrorBuilder {
  message = 'Unknown error'
  severity: AuthErrorSeverity = AuthErrorSeverity.Fatal
  category: AuthErrorCategory = AuthErrorCategory.Unknown
  platform: AuthErrorPlatform = AuthErrorPlatform.Unknown
  domain: AuthErrorDomain = AuthErrorDomain.Unknown
  numeral: AuthErrorNumeral = AuthErrorNumeral.Unspecified
  metadata?: AuthErrorMetadata

  constructor() {
    this.platform = getPlatform()
  }

  // static fromMap(map: Record<string, unknown>): AuthError {
  //   const builder = new AuthErrorBuilder()
  // …build AuthError manually from props?
  //   return builder.build()
  // }

  withMessage(message: string): this {
    this.message = message
    return this
  }

  withSeverity(severity: AuthErrorSeverity): this {
    this.severity = severity
    return this
  }

  withCategory(category: AuthErrorCategory): this {
    this.category = category
    return this
  }

  withPlatform(platform: AuthErrorPlatform): this {
    this.platform = platform
    return this
  }

  withDomain(domain: AuthErrorDomain): this {
    this.domain = domain
    return this
  }

  withNumeral(numeral: AuthErrorNumeral): this {
    this.numeral = numeral
    return this
  }

  withMetadata(metadata: AuthErrorMetadata): this {
    this.metadata = this.metadata || {}
    Object.assign(this.metadata, metadata)
    return this
  }

  private _withMetadataProperty(propertyName: AuthErrorMetadataProperty, value?: string): this {
    if (value) {
      this.metadata = this.metadata || {}
      this.metadata[propertyName] = value
    } else if (this.metadata && propertyName in this.metadata) {
      delete this.metadata[propertyName]
    }
    return this
  }

  withRecoverySuggestion(recoverySuggestion?: string): this {
    return this._withMetadataProperty(AuthErrorMetadataProperty.RecoverySuggestion, recoverySuggestion)
  }

  withSubErrorCode(subErrorCode?: string): this {
    return this._withMetadataProperty(AuthErrorMetadataProperty.SubErrorCode, subErrorCode)
  }

  withSubErrorMessage(subErrorMessage?: string): this {
    return this._withMetadataProperty(AuthErrorMetadataProperty.SubErrorMessage, subErrorMessage)
  }

  withSubErrorUrl(subErrorUrl: string): this {
    return this._withMetadataProperty(AuthErrorMetadataProperty.SubErrorUrl, subErrorUrl)
  }

  getErrorCode(): AuthErrorCode {
    const errorCodeBuilder = new AuthErrorCodeBuilder(
      this.category,
      this.platform,
      this.domain,
      this.numeral
    )
    const errorCode = errorCodeBuilder.build()
    return errorCode
  }

  toString(): string {
    let str = `Auth error occurred:
\tseverity: ${this.severity}
\tcode: ${this.getErrorCode()}
\tmessage: ${this.message}
`
    if (this.metadata) {
      str += '\tmetadata:'
      Object.entries(this.metadata).forEach(([key, value]) => {
        str += `\n\t\t${key}: ${value}`
      })
    }

    return str
  }

  build(): AuthError {
    const { severity, message, metadata } = this
    const code = this.getErrorCode()
    const error: AuthError = {
      message,
      code,
      severity,
      toString: this.toString.bind(this)
    }
    if (metadata) {
      error.metadata = metadata
    }
    
    // For serialization across bridges (iOS/Android), create a plain object without functions
    const serializableError = {
      message: error.message,
      code: error.code,
      severity: error.severity,
      ...(metadata && { metadata })
    }
    
    // Add toString as a non-enumerable property so it doesn't serialize
    Object.defineProperty(serializableError, 'toString', {
      value: this.toString.bind(this),
      enumerable: false,
      writable: false
    })
    
    return serializableError as AuthError
  }
}
